import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useOutletContext } from 'react-router-dom'
import { Box } from '@mui/material'
import moment from 'moment'          

import AlertIcons from 'Pages/Portal/SmartDevice/Smartwatch/components/AlertIcons'
import StatusChip from 'Pages/Portal/Alert/components/StatusChip'
import useFetchData from 'Shared/Hooks/useFetchData'
import { apiUrl } from 'Shared/utils'

import GasMonitor from './components/GasMonitor'
import WorkerMonitor from './components/WorkerMonitor'
import CameraMonitor from './components/CameraMonitor'
import AlertMonitor from './components/AlertMonitor'
import ZoneSlider from './components/ZoneSlider'
import { IconComponent } from 'Shared/Components/Icon'
import { FaShieldAlt } from 'react-icons/fa'
import { gasDetecorMetadata } from 'Shared/constant'
import useLoop from 'Shared/Hooks/useLoop'

const color = ["#008000", "#FFA500", "#FF0000"]

const ConfinedSpaceDashboard = () => {
  const { t } = useTranslation()
  const { send } = useFetchData()
  const { alertTypes, alertEvents, setPageLoading } = useOutletContext()

  const [data, setData] = useState()
  const [threshold, setThreshold] = useState({})
  const [space, setSpace] = useState()
  
  const loadThreshold = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/gas_detector/threshold`,
      returnType: "json"
    })

    const th = {}

    for (const [k, v] of Object.entries(body)) {
      th[k] = []

      for (const i of v) {
        const v0 = i["range"][0] ?? gasDetecorMetadata[k]["plotRange"][0]
        const v1 = i["range"][1] ?? gasDetecorMetadata[k]["plotRange"][1]

        th[k].push({ value: v1 - v0, color: color[i["alert"]] })
      }
    }

    setThreshold(th)
  }

  const loadData = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/dashboard/confined_space`,
      returnType: "json"
    })

    const d = body.map((i) => ({
      "location": i["location"],
      "location_name": i["location_name"],
      "address": i["address"],
      "ptw": i["ptw"],
      // "smart_lock": i["smart_lock"],
      "zones": i["zones"],
      "workers": i["workers"].map((j) => ({
        "worker_id": j["worker_id"],
        "worker_name": <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
          <span>{j["worker_name"]}</span>
          <span>{j["standby_person"] && <IconComponent title="Standby Person"><FaShieldAlt /></IconComponent>}</span>
        </div>,
        "zone_id": j["zone"],
        "zone_name": j["zone_name"],
        "in_tunnel": j["in_tunnel"],
        "alerts": <AlertIcons data={j["alerts"]} />,
        "last_update": moment(j["last_update"]).format("YYYY-MM-DD HH:mm:ss")
      })),
      "worker_num": i["worker_num"],
      "worker_alert": i["worker_alert"],
      "gasValues": i["gasValues"],
      "cameras": i["cameras"].map((j) => ({
        "online": (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", textAlign: "center" }}>
            <span style={{ height: "15px", width: "15px", backgroundColor: j["online"] ? "green" : "red", borderRadius: "50%" }}></span>
          </div>
        ),
        "name": j["name"],
        "camera_id": j["camera_id"]
      })),
      "alerts": i["alerts"].map((j) => ({
        "datetime": j["datetime"],
        "message": `${t(alertTypes[j["type"]])} (${t(alertEvents[j["type"]][j["event"]])})`,
        "target": j["target"],
        "status": <StatusChip status={j["status"]} />
      }))
    }))

    setData(d)
    setSpace(d[0])
  }

  useEffect(() => {
    const init = async () => {
      setPageLoading(true)

      await Promise.all([
        loadThreshold(),
        loadData()
      ])

      setPageLoading(false)
    }

    init()
  }, [])

  useLoop(loadData, 60000)

  return (<>
    <div className="mainContainer" style={{ backgroundColor: "#808080", borderRadius: "15px", padding: "5px" }}>
      <div style={{ overflow: "auto", height: "100%" }}>
        <Box sx={{ display: "flex", flexFlow: "column", height: "100%", width: "100%", minHeight: { xs: "1800px", lg: "900px" }, minWidth: { xs: "750px", lg: "1300px" } }}>
          {(data && space) && <>
            <div style={{ padding: "10px 30px 10px 30px" }}>
              <ZoneSlider data={data} space={space} setSpace={setSpace}/>
            </div>

            <div style={{ display: "flex", flexFlow: "row", height: "100%", width: "100%", borderRadius: "15px", padding: "5px" }}>
              <div style={{ width: "100%", height: "100%", display: "flex" }}>
                <Box style={{ flexGrow: 1, height: "100%", paddingLeft: "5px" }}>
                  <Box sx={{ height: "50%", display: "flex", flexDirection: { xs: "column", lg: "row" } }}>
                    <Box sx={{ width: { xs: "100%", lg: "50%" }, height: { xs: "50%", lg: "100%" }, padding: { xs: "10px 0px 5px 0px", lg: "10px 5px 5px 0px" } }}>
                      <GasMonitor data={space["gasValues"]} threshold={threshold}/>
                    </Box>

                    <Box sx={{ width: { xs: "100%", lg: "50%" }, height: { xs: "50%", lg: "100%" }, padding: { xs: "5px 0px 5px 0px", lg: "10px 0px 5px 5px" } }}>
                      <WorkerMonitor data={space["workers"]} zones={space["zones"]} />
                    </Box>
                  </Box>

                  <Box sx={{ height: "50%", display: "flex", flexDirection: { xs: "column", lg: "row" } }}>
                    <Box sx={{ width: { xs: "100%", lg: "50%" }, height: { xs: "50%", lg: "100%" }, padding: { xs: "5px 0px 5px 0px", lg: "5px 5px 10px 0px" } }}>
                      <CameraMonitor data={space["cameras"]} />
                    </Box>

                    <Box sx={{ width: { xs: "100%", lg: "50%" }, height: { xs: "50%", lg: "100%" }, padding: { xs: "5px 0px 10px 0px", lg: "5px 0px 10px 5px" } }}>
                      <AlertMonitor data={space["alerts"]} />
                    </Box>
                  </Box>
                </Box>
              </div>
            </div>
          </>}
        </Box>
      </div>
    </div>
  </>)
}

export default ConfinedSpaceDashboard
